import {
  AGENT_RECURSION_LIMIT,
  ModelConfig,
  ModelId,
  normalizeReasoningEffort,
  ReasoningEffort,
} from "@config";
import {
  useGetDevices,
  useGetDocumentsTemplates,
  useGetMcpConnections,
  useGetUser,
} from "@hooks";
import { Add, Close, History } from "@mui/icons-material";
import { Skeleton } from "@mui/material";
import type { TemplateConfig } from "@types";
import {
  buildPrompt,
  captureSentryException,
  cn,
  fillRouteWithSlugs,
} from "@utils";
import { PostHogFeature } from "posthog-js/react";
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useAgentThreads } from "src/hooks/useAgentThreads";
import { v4 as uuidv4 } from "uuid";
import { PageBreadcrumb } from "../PageBreadcrumb";
import {
  attachmentContext,
  attachmentModelError,
  AttachmentSaveError,
  imageBlocks,
  messageAttachments,
} from "./attachments";
import {
  AutoScrollContainer,
  ScrollToBottomButton,
} from "./AutoScrollContainer.tsx";

import { usePromptInjectionListener } from "../../hooks/useToolActions.tsx";
import { useStore } from "../../stores/index.ts";
import { ensureToolCallsHaveResponses } from "./ensure-tool-responses.ts";
import { useFileUpload } from "./hooks/use-file-upload.tsx";
import {
  extractReferencedDocuments,
  extractUserQueryContent,
} from "./messages/utils/utils.ts";
import {
  type FormlyStreamRespondOptions,
  type FormlyStreamSubmitOptions,
  useStreamContext,
} from "./Stream.tsx";
import { TooltipIconButton } from "./TooltipIconButton.tsx";
import {
  type ChatSkillContext,
  type ChatSubmitMessage,
  getChatMessageType,
} from "./types.ts";
import { useChatDeviceContext } from "./useChatDeviceContext.ts";

import { observer } from "mobx-react-lite";
import {
  ContentBlock,
  DocumentContentBlock,
  GenericContentBlock,
  TaskContentBlock,
} from "src/components/Chat/ContentBlocksPreview.tsx";
import {
  buildAttachedSkillsPromptContext,
  buildSkillContextsPromptContext,
  useChatSkillRuntime,
} from "src/components/Skills/Chat/useChatSkillRuntime.ts";

// Context-related imports
import { AssistantProcessStatus } from "@models";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { useGetAssistantProcesses } from "src/hooks/queries/assistant-process";
import { useGetTaskPrograms } from "src/hooks/queries/task-program-mutations";
import { useGetTaskStates } from "src/hooks/queries/task-state";
import { EntitlementType } from "src/stores/models";
import { PromptInjectPayload } from "src/stores/tool-actions.ts";
import { hasFunctionalGroupEntitlement } from "src/utils/entitlements";
import { PageHeader } from "../PageHeader/PageHeader.tsx";
import { PageHeaderSkeleton } from "../Skeletons/PageHeaderSkeleton.tsx";
import { AssistantSelector } from "./AssistantSelector.tsx";
import { ChatInput } from "./ChatInput.tsx";
import {
  buildHumanMessageRejectDecisionMessage,
  getHumanInTheLoopRequest,
  type HumanInTheLoopResponse,
} from "./human-in-the-loop.ts";
import { MessagesList } from "./MessagesList.tsx";
import {
  type PendingHumanInTheLoopInterrupt,
  PendingHumanInTheLoopReviews,
} from "./PendingHumanInTheLoopReviews.tsx";
import { ThreadHistoryMenu } from "./ThreadHistoryMenu.tsx";
import { useInvalidateAnswersOnDataKeyApply } from "./useInvalidateAnswersOnDataKeyApply";
import { useInvalidateQualityEventsOnToolApply } from "./useInvalidateQualityEventsOnToolApply";

const EMPTY_TEMPLATE_CONFIG: TemplateConfig = {};

const isNoPendingInterruptError = (error: unknown) => {
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : "";

  return message.includes("No pending interrupt to respond to");
};

const getPendingHumanInTheLoopInterrupts = (
  stream: ReturnType<typeof useStreamContext>
) => {
  let routingReady = true;
  const interrupts: PendingHumanInTheLoopInterrupt[] =
    stream.interrupts.flatMap((interrupt) => {
      const request = getHumanInTheLoopRequest(interrupt);
      if (!request || !interrupt.id) return [];
      const namespace = interrupt.namespace;
      const subagent = [...stream.subagents.values()]
        .filter(
          (agent) =>
            agent.namespace.length > 0 &&
            agent.namespace.every((part, index) => namespace?.[index] === part)
        )
        .sort((a, b) => b.namespace.length - a.namespace.length)[0];
      if (!namespace || (namespace.length > 0 && !subagent))
        routingReady = false;
      return {
        request,
        interruptId: interrupt.id,
        namespace: namespace ?? [],
        subagentId: subagent?.id,
        agentName:
          subagent?.name ?? (namespace?.length ? "Subagent" : "Main agent"),
      };
    });
  return { interrupts, routingReady };
};

interface ThreadProps {
  onClose?: () => void;
  setMaxWidth?: boolean;
}

export const Thread = observer(function Thread({
  onClose,
  setMaxWidth = false,
}: ThreadProps) {
  const { orgId, deviceId } = useChatDeviceContext();
  const { docId } = useParams<{ docId?: string }>();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: user } = useGetUser();
  const { threads, isFetching: threadsLoading } = useAgentThreads();
  if (!orgId) {
    throw new Error("orgId is not set");
  }

  const backendUrl = window._env_.BACKEND_BASE_URL;

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<null | HTMLElement>(
    null
  );

  const stream = useStreamContext();
  const messages = stream.messages;
  const isLoading = stream.isLoading;
  const isThreadLoading = stream.isThreadLoading;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [input, setInput] = useState("");
  const {
    contentBlocks: fileBlocks,
    setContentBlocks: setFileBlocks,
    handleFileUpload,
    removeBlock: removeAttachment,
    retryBlock: retryAttachment,
    setSaveToEqms,
    prepareForSend,
    dropRef,
    dragOver,
    handlePaste,
  } = useFileUpload({ disabled: isLoading || isSubmitting });

  // Combined blocks state for documents and tasks (selected from context menu)
  const [contextBlocks, setContextBlocks] = useState<
    Array<DocumentContentBlock | TaskContentBlock>
  >([]);
  const [skillContexts, setSkillContexts] = useState<ChatSkillContext[]>([]);
  const [showAssistantProcessBlock, setShowAssistantProcessBlock] =
    useState(true);
  const { data: devices = [] } = useGetDevices({ orgId, enabled: !!orgId });
  const { data: mcpConnections } = useGetMcpConnections(orgId, true);
  const canAdministerAllTrainings =
    !!user &&
    (user.permissions?.includes("write:configuration") ||
      hasFunctionalGroupEntitlement(
        user,
        orgId,
        EntitlementType.ADMINISTER_ALL_TRAININGS
      ));
  const { refetch: refetchTrainingPrograms } = useGetTaskPrograms(
    orgId,
    "all",
    { enabled: false }
  );

  // Check if we're in change management mode and get the assistant process ID
  const isChangeManagementUrl = pathname.includes("/assistant/document-update");
  const { data: assistantProcesses } = useGetAssistantProcesses({
    orgId,
    deviceId,
    status: AssistantProcessStatus.IN_PROGRESS,
    enabled: isChangeManagementUrl && !!deviceId,
  });
  const assistantProcessId = isChangeManagementUrl
    ? assistantProcesses?.[0]?.id
    : undefined;

  useEffect(() => {
    if (assistantProcessId) {
      setShowAssistantProcessBlock(true);
    }
  }, [assistantProcessId]);

  const genericBlocks: GenericContentBlock[] =
    assistantProcessId && showAssistantProcessBlock
      ? [
          {
            type: "generic" as const,
            title: "Assistant Process",
            removable: false,
          },
        ]
      : [];

  // Context references selected from the eQMS.
  const contentBlocks: ContentBlock[] = [...contextBlocks, ...genericBlocks];

  const handleAddDocument = (documentBlock: DocumentContentBlock) => {
    setContextBlocks((prev) => [...prev, documentBlock]);
  };

  const handleAddTask = (taskBlock: TaskContentBlock) => {
    setContextBlocks((prev) => [...prev, taskBlock]);
  };

  const handleCancel = async () => {
    // get the current run for the thread
    stream.stop();

    if (agentStore.currentThreadId) {
      const runs = await stream.client.runs.list(agentStore.currentThreadId);
      if (runs.length > 0) {
        runs.forEach((run) => {
          if (
            (run.status === "running" ||
              run.status === "pending" ||
              // @ts-ignore langgraph-sdk types are not up to date. runs appear to have type busy as well
              run.status === "busy") &&
            !!agentStore.currentThreadId
          ) {
            stream.client.runs.cancel(agentStore.currentThreadId, run.run_id);
          }
        });
      }
    }
  };

  const handleRemoveBlock = (idx: number) => {
    setContextBlocks((prev) => prev.filter((_, i) => i !== idx));
  };
  const pendingMessage = useRef<{ signature: string; id: string } | null>(null);
  const submitting = useRef(false);
  const composerGeneration = useRef(0);
  const [firstTokenReceived, setFirstTokenReceived] = useState(false);

  const { agentStore } = useStore();

  useInvalidateAnswersOnDataKeyApply({
    messages,
    orgId,
  });
  useInvalidateQualityEventsOnToolApply({
    messages,
    orgId,
  });

  const chatStarted = !!agentStore.currentThreadId || !!messages;
  const hasNoAIOrToolMessages = !messages.find((m) => {
    const messageType = getChatMessageType(m);
    return messageType === "ai" || messageType === "tool";
  });
  const approvalThreadId = agentStore.currentThreadId ?? stream.threadId;
  const {
    interrupts: pendingHumanInTheLoopInterrupts,
    routingReady: approvalRoutingReady,
  } = getPendingHumanInTheLoopInterrupts(stream);
  const approvalsVisible =
    !isThreadLoading && !isLoading && approvalRoutingReady;

  const selectedModelConfig = agentStore.selectedModelConfig;
  const attachmentError = attachmentModelError(selectedModelConfig.provider, [
    ...fileBlocks.flatMap((a) => (a.reference ? [a.reference] : [])),
    ...messages.flatMap((m) =>
      messageAttachments(m.additional_kwargs?.attachments)
    ),
  ]);

  const selectedReasoningEffort = agentStore.selectedReasoningEffort;
  const selectedAssistantConfigurable = useMemo(() => {
    const configurable = agentStore.selectedAssistant?.config?.configurable;
    if (
      !configurable ||
      typeof configurable !== "object" ||
      Array.isArray(configurable)
    ) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(configurable as Record<string, unknown>).filter(
        ([key]) => key !== "device_id"
      )
    );
  }, [agentStore.selectedAssistant?.config?.configurable]);
  // Form ref for programmatic submission
  const formRef = useRef<HTMLFormElement>(null);
  const [approvalContainer, setApprovalContainer] =
    useState<HTMLDivElement | null>(null);

  // Flag to track when auto-submission should happen
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);

  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId: orgId,
    deviceId: deviceId,
    enabled: !!deviceId,
  });
  const { data: taskStates = [] } = useGetTaskStates({
    orgId,
    deviceId,
    enabled: !!orgId && !!deviceId,
  });
  const {
    attachedSkills,
    handleAttachedSkillsChange,
    handleCreateSkillFromChat,
    isCreateSkillFromChatDisabled,
    resetAttachedSkillsForThreadChange,
  } = useChatSkillRuntime({
    orgId,
    isLoading,
    setInput,
    setShouldAutoSubmit,
  });

  const handleModelSelect = (modelConfig: ModelConfig) => {
    agentStore.setSelectedModelConfig(modelConfig);
  };

  const handleReasoningEffortSelect = (reasoningEffort: ReasoningEffort) => {
    agentStore.setSelectedReasoningEffort(reasoningEffort);
  };

  // Handle prompt injection from other components
  const handlePromptReceived = async ({
    prompt,
    replaceInput,
    submit,
    newThread,
    contextConfig,
    skillContexts: injectedSkillContexts,
  }: PromptInjectPayload & { eventId: string }) => {
    if (newThread) handleSetThreadId(null);

    // Extract user query from the prompt
    const userQuery = extractUserQueryContent(prompt);

    // Extract referenced documents and data keys from the prompt
    const referencedBlocks = extractReferencedDocuments(prompt);

    // Convert contextConfig to document blocks for data keys
    const contextBlocks: DocumentContentBlock[] = [];
    if (contextConfig) {
      contextConfig.forEach((dataKeyInfo, dataKeyId) => {
        if (dataKeyInfo.complete && dataKeyInfo.value) {
          contextBlocks.push({
            type: "document",
            documentId: `data-key-${dataKeyId}`,
            documentTitle: dataKeyId,
            templateKey: "data-key",
            templateTitle: "Data Key",
            dataKeyId: dataKeyId as any,
            metadata: {
              documentId: `data-key-${dataKeyId}`,
              documentTitle: dataKeyId,
              templateKey: "data-key",
              templateTitle: "Data Key",
              dataKeyId: dataKeyId as any,
            },
          });
        }
      });
    }

    if (userQuery) {
      // Use the extracted user query as input
      if (replaceInput) {
        setInput(userQuery);
      } else {
        setInput((prev) => (prev ? `${prev}\n${userQuery}` : userQuery));
      }

      // Add referenced documents/data keys and context blocks
      const allBlocks = [...referencedBlocks, ...contextBlocks];
      if (allBlocks.length > 0) {
        setContextBlocks((prev) => [...prev, ...allBlocks]);
      }
    } else {
      // Fallback to original prompt if no user query found
      if (replaceInput) {
        setInput(prompt);
      } else {
        setInput((prev) => (prev ? `${prev}\n${prompt}` : prompt));
      }
    }

    if (injectedSkillContexts) {
      setSkillContexts(injectedSkillContexts);
    } else if (replaceInput) {
      setSkillContexts([]);
    }

    // Auto-submit if requested
    if (submit) {
      setShouldAutoSubmit(true);
    }
  };

  const mapErrorToMessage = (error: any) => {
    if (error instanceof AttachmentSaveError) return error.message;
    if (isNoPendingInterruptError(error)) {
      return "This approval is no longer active. Reload the conversation.";
    }

    if (typeof error === "string") {
      if (error.includes("overloaded_error")) {
        return "Overloaded. The agent hit its limit. Please try again later.";
      }
      if (error.includes("GraphRecursionError")) {
        return "Recursion limit reached. Please try simplifying your request or break it into smaller parts.";
      }
      if (error.includes("CancelledError")) {
        return "The agent was cancelled.";
      }
    }

    if (typeof error === "object") {
      if (error.name?.includes("Overloaded")) {
        return "Overloaded. The agent hit its limit. Please try again later.";
      }
      if (error.name?.includes("GraphRecursionError")) {
        return "Recursion limit reached. Please try simplifying your request or break it into smaller parts.";
      }
      if (error.name?.includes("CancelledError")) {
        return "The agent was cancelled. Please try again.";
      }
      if (error.name?.includes("UserInterrupt")) {
        return "The agent was interrupted.";
      }
    }

    captureSentryException(error);

    return "An error occurred while processing your request.";
  };

  usePromptInjectionListener(handlePromptReceived);

  // Auto-submit effect - triggers immediately when state is ready
  useEffect(() => {
    if (shouldAutoSubmit && formRef.current) {
      // Check if we have content to submit
      const hasContent =
        input.trim().length > 0 ||
        contentBlocks.length > 0 ||
        fileBlocks.length > 0;

      if (!hasContent || isLoading || isThreadLoading) {
        setShouldAutoSubmit(false); // Reset the flag
        return;
      }

      formRef.current.requestSubmit();
      setShouldAutoSubmit(false); // Reset the flag
    }
  }, [shouldAutoSubmit, input, contentBlocks, isLoading, isThreadLoading]);

  const handleSetThreadId = (id: string | null) => {
    composerGeneration.current += 1;
    submitting.current = false;
    pendingMessage.current = null;
    setIsSubmitting(false);
    setInput("");
    setFileBlocks([]);
    setContextBlocks([]);
    setShouldAutoSubmit(false);
    resetAttachedSkillsForThreadChange();
    setSkillContexts([]);
    agentStore.navigateToThread(id);
  };

  useEffect(() => {
    agentStore.initializeFromUrl();
  }, []);

  // TODO: this should be part of the useStream hook
  const prevMessageLength = useRef(0);
  useEffect(() => {
    if (
      messages.length !== prevMessageLength.current &&
      messages?.length &&
      getChatMessageType(messages[messages.length - 1]) === "ai"
    ) {
      setFirstTokenReceived(true);
    }

    prevMessageLength.current = messages.length;
  }, [messages]);

  const buildBaseRunOptions = useCallback(
    ({
      userId,
      orgId,
    }: {
      userId: string;
      orgId: string;
    }): FormlyStreamRespondOptions => {
      // Determine reasoning effort - only include if the model supports it
      const reasoningEffort = normalizeReasoningEffort(
        selectedModelConfig,
        selectedReasoningEffort
      );

      const runContext = {
        organization_id: orgId,
        user_id: userId,
        be_url: backendUrl,
        model_provider: selectedModelConfig.provider,
        model_name: selectedModelConfig.modelName,
        max_output_tokens: selectedModelConfig.maxOutputTokens,
        context_window: selectedModelConfig.contextWindow,
        // Only include optional fields if defined
        ...(selectedModelConfig.temperature !== undefined && {
          temperature: selectedModelConfig.temperature,
        }),
        ...(selectedModelConfig.contextSummarizationThreshold !== undefined && {
          context_summarization_threshold:
            selectedModelConfig.contextSummarizationThreshold,
        }),
        ...(reasoningEffort && { reasoning_effort: reasoningEffort }),
        ...(assistantProcessId && {
          assistant_process_id: assistantProcessId,
        }),
      };

      const configurable: Record<string, unknown> = {
        ...selectedAssistantConfigurable,
        ...runContext,
      };
      if (selectedModelConfig.id === ModelId.CLAUDE_SONNET_5_5) {
        for (const parameter of [
          "temperature",
          "top_p",
          "top_k",
          "reasoning",
          "reasoning_budget",
        ]) {
          delete configurable[parameter];
        }
      }

      return {
        metadata: {
          organization_id: orgId,
          user_id: userId,
        },
        config: {
          recursion_limit: AGENT_RECURSION_LIMIT,
          configurable,
        },
      };
    },
    [
      selectedModelConfig,
      selectedReasoningEffort,
      selectedAssistantConfigurable,
      backendUrl,
      assistantProcessId,
    ]
  );

  const submitMessages = useCallback(
    async ({
      messages,
      userId,
      orgId,
      parentCheckpoint,
    }: {
      messages: ChatSubmitMessage[] | undefined;
      userId: string;
      orgId: string;
      parentCheckpoint: { checkpoint_id?: string } | null | undefined;
    }) => {
      const generation = composerGeneration.current;
      agentStore.setError(null);
      const submitOptions: FormlyStreamSubmitOptions = {
        ...buildBaseRunOptions({
          userId,
          orgId,
        }),
        ...(parentCheckpoint?.checkpoint_id && {
          forkFrom: parentCheckpoint.checkpoint_id,
        }),
      };

      let failed = false;
      await stream.submit(
        messages !== undefined ? { messages: messages } : undefined,
        {
          ...submitOptions,
          onError: (error) => {
            failed = true;
            if (generation === composerGeneration.current) {
              agentStore.setError(error);
            }
          },
        }
      );
      return !failed;
    },
    [stream, buildBaseRunOptions, agentStore]
  );

  const respondToPendingHumanInTheLoopInterrupts = useCallback(
    async (responses: HumanInTheLoopResponse[]) => {
      if (!user?.id) throw new Error("Sign in to review tool calls.");
      if (
        !approvalRoutingReady ||
        responses.length !== pendingHumanInTheLoopInterrupts.length
      ) {
        throw new Error(
          "The pending approvals changed. Reload the conversation."
        );
      }
      agentStore.setError(null);
      try {
        const options = buildBaseRunOptions({
          userId: user.id,
          orgId,
        });
        if (responses.length === 1) {
          const { interruptId, namespace } = pendingHumanInTheLoopInterrupts[0];
          await stream.respond(responses[0], {
            ...options,
            interruptId,
            namespace,
          });
        } else {
          await stream.respondAll(
            Object.fromEntries(
              pendingHumanInTheLoopInterrupts.map(({ interruptId }, index) => [
                interruptId,
                responses[index],
              ])
            ),
            options
          );
        }
      } catch (error) {
        if (isNoPendingInterruptError(error)) captureSentryException(error);
        agentStore.setError(error);
        throw error;
      }
    },
    [
      agentStore,
      approvalRoutingReady,
      buildBaseRunOptions,
      orgId,
      pendingHumanInTheLoopInterrupts,
      stream,
      user?.id,
    ]
  );

  useEffect(() => {
    if (searchParams.get("joinStream") === "true") {
      setSearchParams(
        (prev) => {
          prev.delete("joinStream");
          return prev;
        },
        { replace: true }
      );
    }
  }, []);

  const handleSubmit = async (e: FormEvent, orgId: string, userId: string) => {
    e.preventDefault();
    const generation = composerGeneration.current;

    if (
      (input.trim().length === 0 &&
        contentBlocks.length === 0 &&
        fileBlocks.length === 0) ||
      submitting.current ||
      !!attachmentError ||
      fileBlocks.some((a) => a.status !== "ready") ||
      isLoading ||
      isThreadLoading ||
      !approvalRoutingReady
    ) {
      setShouldAutoSubmit(false); // Reset auto-submit flag if submission is blocked
      return;
    }

    setShouldAutoSubmit(false); // Reset auto-submit flag on successful submission
    setFirstTokenReceived(false);
    if (assistantProcessId) {
      setShowAssistantProcessBlock(false);
    }

    if (pendingHumanInTheLoopInterrupts.length > 0) {
      if (fileBlocks.length) {
        agentStore.setError(
          new Error(
            "Respond to the pending approval before sending attachments."
          )
        );
        return;
      }
      const userMessage = input.trim();
      const rejectDecision =
        userMessage.length > 0
          ? ({
              type: "reject",
              message: buildHumanMessageRejectDecisionMessage(userMessage),
            } as const)
          : ({ type: "reject" } as const);

      try {
        await respondToPendingHumanInTheLoopInterrupts(
          pendingHumanInTheLoopInterrupts.map(({ request }) => ({
            decisions: request.action_requests.map(() => rejectDecision),
          }))
        );
      } catch {
        return;
      }

      if (generation !== composerGeneration.current) return;
      setInput("");
      setFileBlocks([]);
      setContextBlocks([]);
      setSkillContexts([]);
      return;
    }

    submitting.current = true;
    setIsSubmitting(true);
    try {
      const preparedAttachments = await prepareForSend();
      if (generation !== composerGeneration.current) return;
      // Convert document blocks to document references for the context
      let referencedDataKeys: string = "";
      let referencedDocuments: string = "";
      let referencedTasks: string = "";
      contentBlocks.forEach((block) => {
        if (block.type === "document") {
          const deviceReference = block.deviceId
            ? `<device-id>${block.deviceId}</device-id>\n`
            : "";
          if (block.dataKeyId) {
            const referenceKey = `<data-key>\n${block.dataKeyId}\n${deviceReference}</data-key>\n`;
            referencedDataKeys += referenceKey;
          } else {
            const referenceKey = `<document>\n<name>${block.documentTitle}</name>\n<id>${block.documentId}</id>\n${deviceReference}</document>\n`;
            referencedDocuments += referenceKey;
          }
        }
        if (block.type === "task") {
          const notesDocumentId = block.notesDocumentId
            ? `<notes-document-id>${block.notesDocumentId}</notes-document-id>\n`
            : "";
          const fileDocuments =
            block.fileDocumentIds.length > 0
              ? `<file-documents>\n${block.fileDocumentIds
                  .map((docId) => `<document-id>${docId}</document-id>`)
                  .join("\n")}\n</file-documents>\n`
              : "";
          const referenceKey = `<task>\n<name>${block.taskTitle}</name>\n<task-state-id>${block.taskStateId}</task-state-id>\n${notesDocumentId}${fileDocuments}</task>\n`;
          referencedTasks += referenceKey;
        }
      });

      // Get the current URL
      const currentUrl = window.location.href;

      // Build the additional context with URL, data keys, and documents
      let additionalContextParts: string[] = [];

      // Add current URL
      additionalContextParts.push(
        `<current-url>\n${currentUrl}\n</current-url>`
      );

      if (docId && canAdministerAllTrainings) {
        const { data: trainingPrograms = [] } = await refetchTrainingPrograms();
        if (generation !== composerGeneration.current) return;
        const documentTrainingProgram = trainingPrograms.find(
          (program) => program.enabled && program.linkedDocumentId === docId
        );
        const documentVersionId = searchParams.get("v");
        const documentVersionContext = documentVersionId
          ? `<document-version-id>${documentVersionId}</document-version-id>`
          : "";
        if (documentTrainingProgram) {
          const editTrainingUrl = fillRouteWithSlugs(ROUTES.TRAINING_EDIT, {
            [ROUTE_SLUGS.ORG_ID]: orgId,
            [ROUTE_SLUGS.PROGRAM_ID]: documentTrainingProgram.id,
          });
          additionalContextParts.push(
            [
              "<training-program-context>",
              "<status>existing-training-program</status>",
              `<document-id>${docId}</document-id>`,
              documentVersionContext,
              `<program-id>${documentTrainingProgram.id}</program-id>`,
              `<completion-method>${documentTrainingProgram.completionMethod}</completion-method>`,
              `<edit-training-url>${editTrainingUrl}</edit-training-url>`,
              "</training-program-context>",
            ].join("\n")
          );
        } else {
          additionalContextParts.push(
            [
              "<training-program-context>",
              "<status>missing-training-program</status>",
              `<document-id>${docId}</document-id>`,
              documentVersionContext,
              "</training-program-context>",
            ].join("\n")
          );
        }
      }

      // Add referenced data keys
      if (referencedDataKeys) {
        additionalContextParts.push(
          `<referenced-data-keys>\n${referencedDataKeys}</referenced-data-keys>`
        );
      }

      // Add referenced documents
      if (referencedDocuments) {
        additionalContextParts.push(
          `<referenced-documents>\n${referencedDocuments}</referenced-documents>`
        );
      }

      // Add referenced tasks
      if (referencedTasks) {
        additionalContextParts.push(
          `<referenced-tasks>\n${referencedTasks}</referenced-tasks>`
        );
      }

      const attachedSkillsContext =
        buildAttachedSkillsPromptContext(attachedSkills);
      if (attachedSkillsContext) {
        additionalContextParts.push(attachedSkillsContext);
      }
      const messageSkillContext =
        buildSkillContextsPromptContext(skillContexts);
      if (messageSkillContext) {
        additionalContextParts.push(messageSkillContext);
      }

      if (
        isChangeManagementUrl &&
        assistantProcesses?.[0] &&
        hasNoAIOrToolMessages
      ) {
        additionalContextParts.push(
          `<change-process-state>\n${JSON.stringify(
            assistantProcesses[0],
            null,
            4
          )}\n</change-process-state>`
        );
      }

      // Build the message text with the input and referenced context
      const attachmentsContext = attachmentContext(preparedAttachments);
      if (attachmentsContext) additionalContextParts.push(attachmentsContext);
      const messageText = buildPrompt({
        instructions: input,
        additionalContext: additionalContextParts.join("\n"),
      });

      const signature = JSON.stringify([
        input,
        orgId,
        contextBlocks,
        attachedSkills,
        skillContexts,
        preparedAttachments.map((a) => [
          a.reference?.id,
          a.reference?.documentId,
        ]),
      ]);
      if (pendingMessage.current?.signature !== signature)
        pendingMessage.current = { signature, id: uuidv4() };
      const newMessage: ChatSubmitMessage = {
        id: pendingMessage.current.id,
        type: "human",
        role: "user",
        content: [
          { type: "text", text: messageText },
          ...imageBlocks(preparedAttachments),
        ],
        additional_kwargs: {
          attachments: preparedAttachments.flatMap((a) =>
            a.reference ? [a.reference] : []
          ),
          skillContexts,
        },
      };

      const toolMessages = ensureToolCallsHaveResponses(stream.messages);
      const messagesToSubmit = [...toolMessages, newMessage];
      const clearedContextBlocks: typeof contextBlocks = [];
      const clearedSkillContexts: typeof skillContexts = [];

      setInput("");
      setContextBlocks(clearedContextBlocks);
      setSkillContexts(clearedSkillContexts);

      let success = false;
      try {
        success = await submitMessages({
          messages: messagesToSubmit,
          userId,
          orgId,
          parentCheckpoint: undefined,
        });
      } finally {
        if (!success && generation === composerGeneration.current) {
          setInput((current) => current || input);
          setContextBlocks((current) =>
            current === clearedContextBlocks ? contextBlocks : current
          );
          setSkillContexts((current) =>
            current === clearedSkillContexts ? skillContexts : current
          );
        }
      }

      if (!success || generation !== composerGeneration.current) return;
      pendingMessage.current = null;
      // reset the optimistic thread i
      setFileBlocks([]);
    } catch (error) {
      if (generation === composerGeneration.current) {
        agentStore.setError(error);
      }
    } finally {
      if (generation === composerGeneration.current) {
        submitting.current = false;
        setIsSubmitting(false);
      }
    }
  };

  const handleHistoryClick = async (event: React.MouseEvent<HTMLElement>) => {
    setHistoryAnchorEl(event.currentTarget);
    setIsHistoryOpen(true);
    try {
      await agentStore.refreshThreads();
    } catch (error) {
      console.error("Error updating threads:", error);
    }
  };

  const handleCloseHistory = () => {
    setIsHistoryOpen(false);
    setHistoryAnchorEl(null);
  };

  if (!user?.id) {
    return (
      <div
        className={cn(
          "flex h-full w-full flex-col gap-4 justify-between",
          setMaxWidth && "max-w-4xl self-center"
        )}
      >
        <PageHeaderSkeleton />
        <div
          className="flex min-h-0 flex-1 flex-col justify-between gap-4 px-3 pb-3"
          data-testid="thread-loading-skeleton-content"
        >
          <div className="flex w-full flex-col gap-4">
            <Skeleton height={45} width={"100%"} className="mb-5" />
            <Skeleton height={70} width={"75%"} />
            <Skeleton height={180} width={"100%"} />
            <Skeleton height={20} width={"95%"} />
          </div>
          <Skeleton height={120} width={"100%"} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full items-center justify-center relative">
      <div className=" relative flex overflow-hidden h-full w-full rounded-lg border-1 border-gray-300">
        <div
          className={cn(
            "grid w-full grid-cols-[1fr_0fr] transition-all duration-500"
          )}
        >
          <div
            className={cn(
              "relative flex min-w-0 flex-1 flex-col overflow-hidden gap-y-2",
              !chatStarted && "grid-rows-[1fr]"
            )}
          >
            {/* Common header that always shows */}
            <PageHeader
              pageTitleComponent={
                <PageBreadcrumb items={[{ label: "Agent" }]} />
              }
              rightButtons={
                <div className="flex items-center gap-2">
                  <PostHogFeature
                    flag="assistant-selection-enabled"
                    match={true}
                    fallback={<div></div>}
                  >
                    <AssistantSelector />
                  </PostHogFeature>
                  <div className="relative flex items-center justify-start gap-2">
                    <div>
                      <TooltipIconButton
                        tooltip="Chat history"
                        onClick={(event) => handleHistoryClick(event)}
                        data-history-button
                      >
                        <History className="size-5" />
                      </TooltipIconButton>
                    </div>
                  </div>
                  <ThreadHistoryMenu
                    onClose={handleCloseHistory}
                    onThreadSelect={handleSetThreadId}
                    historyAnchorEl={historyAnchorEl}
                    isHistoryOpen={isHistoryOpen}
                  />
                  {chatStarted && (
                    <div className="flex items-center gap-4">
                      <TooltipIconButton
                        tooltip="New thread"
                        aria-label="New thread"
                        onClick={() => handleSetThreadId(null)}
                      >
                        <Add className="size-5" />
                      </TooltipIconButton>
                    </div>
                  )}
                  <TooltipIconButton tooltip="Close" onClick={onClose}>
                    <Close className="size-5" />
                  </TooltipIconButton>
                </div>
              }
              rightPanelToggleVisible={false}
              leftPanelToggleVisible={false}
            />

            <div
              className={cn(
                "relative flex-1 overflow-hidden w-full grid grid-rows-[1fr_auto] py-2 px-3",
                setMaxWidth && "max-w-4xl self-center"
              )}
            >
              <AutoScrollContainer
                className="overflow-y-scroll h-full"
                dependencies={[messages, isLoading]}
                fixedContent={
                  <div className="sticky bottom-4 flex justify-center pr-4 pointer-events-none">
                    <ScrollToBottomButton className="animate-in fade-in-0 zoom-in-95 z-10 pointer-events-auto" />
                  </div>
                }
              >
                <div className="pb-6 mx-auto flex flex-col gap-3 w-full overflow-x-clip pr-2">
                  <PendingHumanInTheLoopReviews
                    key={approvalThreadId ?? "new-thread"}
                    approvalContainer={approvalContainer}
                    interrupts={pendingHumanInTheLoopInterrupts}
                    isLoading={isLoading || isThreadLoading}
                    visible={approvalsVisible}
                    onRespond={respondToPendingHumanInTheLoopInterrupts}
                  >
                    <MessagesList
                      threadsLoading={threadsLoading}
                      threadsLength={threads.length}
                      firstTokenReceived={firstTokenReceived}
                      connectionNames={
                        mcpConnections?.connections.map(
                          (connection) => connection.name
                        ) ?? []
                      }
                    />
                  </PendingHumanInTheLoopReviews>
                  {agentStore.error && !isLoading && (
                    <div className="text-orange-500">
                      {mapErrorToMessage(agentStore.error)}
                    </div>
                  )}
                </div>
              </AutoScrollContainer>
              <div className="sticky bottom-0 flex flex-col items-center">
                <div ref={setApprovalContainer} className="w-full" />
                <ChatInput
                  input={input}
                  setInput={setInput}
                  contentBlocks={contentBlocks}
                  isLoading={isLoading}
                  isSubmitDisabled={
                    isThreadLoading || isSubmitting || !approvalRoutingReady
                  }
                  onSubmit={(e) => handleSubmit(e, orgId, user.id)}
                  onStop={handleCancel}
                  handleFileUpload={handleFileUpload}
                  handlePaste={handlePaste}
                  dropRef={dropRef}
                  dragOver={dragOver}
                  templatesConfig={templatesConfig ?? EMPTY_TEMPLATE_CONFIG}
                  devices={devices}
                  formRef={formRef}
                  handleRemoveBlock={handleRemoveBlock}
                  handleAddDocument={handleAddDocument}
                  handleAddTask={handleAddTask}
                  selectedModelConfig={selectedModelConfig}
                  onModelSelect={handleModelSelect}
                  selectedReasoningEffort={selectedReasoningEffort}
                  onReasoningEffortSelect={handleReasoningEffortSelect}
                  uploadedFiles={fileBlocks}
                  onSaveAttachmentChange={setSaveToEqms}
                  onRemoveAttachment={removeAttachment}
                  onRetryAttachment={retryAttachment}
                  attachmentError={attachmentError}
                  tasks={taskStates}
                  attachedSkills={attachedSkills}
                  skillContexts={skillContexts}
                  onAttachedSkillsChange={handleAttachedSkillsChange}
                  onCreateSkillFromChat={handleCreateSkillFromChat}
                  isCreateSkillFromChatDisabled={isCreateSkillFromChatDisabled}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
